<template>
  <div id="klaro"></div>
</template>

<script>
import { onMounted } from 'vue'

export default {
  name: 'KlaroConsent',
  setup() {
    onMounted(() => {
      if (window.klaro && typeof window.klaro.setup === 'function') {
        window.klaro.setup({
          elementID: 'klaro',
          noAutoLoad: false,
          storageMethod: 'cookie',
          cookieName: 'klaro',
          htmlTexts: true,
          default: false,
          mustConsent: true,
          acceptAll: true,
          hideDeclineAll: false,
          // Opcje do ponownego otwierania modala
          embedded: false,
          stylingTheme: ['base', 'top'],
          groupByPurpose: true,
          powerButton: true,
          consentNotice: {
            description: 'Ta strona używa plików cookie do analizy ruchu i poprawy jakości usług.',
            learnMore: 'Dowiedz się więcej',
            testing: 'Tryb testowy!'
          },
          translations: {
            pl: {
              consentModal: {
                title: 'Ustawienia prywatności',
                description: 'Możesz zdecydować, na które kategorie plików cookie wyrażasz zgodę. Szczegóły znajdziesz w naszej polityce prywatności.'
              },
              googleAnalytics: {
                description: 'Statystyki odwiedzin Google Analytics',
                title: 'Google Analytics'
              },
              metaPixel: {
                description: 'Śledzenie konwersji i reklam Facebook',
                title: 'Meta Pixel'
              },
              clarity: {
                description: 'Analiza zachowań użytkowników i nagrywanie sesji',
                title: 'Microsoft Clarity'
              },
              googleAds: {
                description: 'Śledzenie konwersji i reklam Google Ads',
                title: 'Google Ads'
              },
              googleSignin: {
                description: 'Logowanie przez Google',
                title: 'Google Sign-In'
              },
              facebookSdk: {
                description: 'Logowanie przez Facebook',
                title: 'Facebook SDK'
              },
              microsoftSdk: {
                description: 'Logowanie przez Microsoft',
                title: 'Microsoft SDK'
              },
              purposes: {
                analytics: 'Statystyka',
                social: 'Logowanie społecznościowe'
              },
              ok: 'OK',
              acceptAll: 'Zaakceptuj wszystko',
              decline: 'Odrzuć',
              save: 'Zapisz wybór',
              consentNotice: {
                description: 'Ta strona używa plików cookie do analizy ruchu i poprawy jakości usług.',
                learnMore: 'Dowiedz się więcej',
                testing: 'Tryb testowy!'
              },
              // Dodatkowe tłumaczenia dla przycisku ustawień
              privacySettings: 'Ustawienia prywatności',
              privacySettingsUrl: '/privacy-policy',
              poweredBy: 'Powered by Klaro'
            }
          },
          services: (() => {
            const services = [
              {
                name: 'essential',
                title: 'Niezbędne pliki cookie',
                description: 'Te pliki cookie są wymagane do prawidłowego działania serwisu, logowania i utrzymania sesji. Nie można ich wyłączyć.',
                purposes: ['essential'],
                cookies: ['token', 'auth_token', 'sessionid', 'XSRF-TOKEN', 'klaro'],
                required: true,
                default: true,
                onlyOnce: true
              },
              {
                name: 'google-signin',
                title: 'Google Sign-In',
                purposes: ['essential'],
                cookies: [/^gsi/, /^google/],
                required: true,
                default: true,
                onlyOnce: true
                // Klaro automatycznie obsłuży skrypty z type="text/plain"
              },
              {
                name: 'facebook-sdk',
                title: 'Facebook SDK',
                purposes: ['essential'],
                cookies: [/^fb/, /^_fbp/, /^_fbc/],
                required: true,
                default: true,
                onlyOnce: true
                // Klaro automatycznie obsłuży skrypty z type="text/plain"
              },
              {
                name: 'microsoft-sdk',
                title: 'Microsoft SDK',
                purposes: ['essential'],
                cookies: [/^msal/, /^microsoft/],
                required: true,
                default: true,
                onlyOnce: true
                // Klaro automatycznie obsłuży skrypty z type="text/plain"
              }
            ]

            // Dodaj Google Analytics tylko na produkcji
            if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
              services.push({
                name: 'google-analytics',
                title: 'Google Analytics',
                purposes: ['analytics'],
                cookies: [/^_ga/, /^_gid/, /^_gat/],
                required: false,
                default: false,
                onlyOnce: true,
                onAccept: () => {
                  // Wywołaj zdarzenie gdy użytkownik zaakceptuje Google Analytics
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'google-analytics': true }
                    }
                  }))
                },
                onDecline: () => {
                  // Usuwanie cookies GA
                  document.cookie = '_ga=; Max-Age=0; path=/';
                  document.cookie = '_gid=; Max-Age=0; path=/';
                  document.cookie = '_gat=; Max-Age=0; path=/';
                  
                  // Wywołaj zdarzenie gdy użytkownik odrzuci Google Analytics
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'google-analytics': false }
                    }
                  }))
                }
              })
            }

            // Dodaj Meta Pixel tylko na produkcji
            if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
              services.push({
                name: 'meta-pixel',
                title: 'Meta Pixel',
                purposes: ['analytics'],
                cookies: [/^_fbp/, /^_fbc/],
                required: false,
                default: false,
                onlyOnce: true,
                onAccept: () => {
                  // Wywołaj zdarzenie gdy użytkownik zaakceptuje Meta Pixel
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'meta-pixel': true }
                    }
                  }))
                },
                onDecline: () => {
                  // Usuwanie cookies Meta Pixel
                  document.cookie = '_fbp=; Max-Age=0; path=/';
                  document.cookie = '_fbc=; Max-Age=0; path=/';
                  
                  // Wywołaj zdarzenie gdy użytkownik odrzuci Meta Pixel
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'meta-pixel': false }
                    }
                  }))
                }
              })
            }

            // Dodaj Microsoft Clarity tylko na produkcji
            if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
              services.push({
                name: 'clarity',
                title: 'Microsoft Clarity',
                purposes: ['analytics'],
                cookies: [/^_clck/, /^_clsk/],
                required: false,
                default: false,
                onlyOnce: true,
                onAccept: () => {
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'clarity': true }
                    }
                  }))
                },
                onDecline: () => {
                  // Usuwanie cookies Microsoft Clarity
                  document.cookie = '_clck=; Max-Age=0; path=/';
                  document.cookie = '_clsk=; Max-Age=0; path=/';
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'clarity': false }
                    }
                  }))
                }
              })
            }

            // Dodaj Google Ads tylko na produkcji
            if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
              services.push({
                name: 'google-ads',
                title: 'Google Ads',
                purposes: ['analytics'],
                cookies: [/^_gac/, /^_gcl/, /^_gcl_au/],
                required: false,
                default: false,
                onlyOnce: true,
                onAccept: () => {
                  // Wywołaj zdarzenie gdy użytkownik zaakceptuje Google Ads
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'google-ads': true }
                    }
                  }))
                },
                onDecline: () => {
                  // Usuwanie cookies Google Ads
                  document.cookie = '_gac=; Max-Age=0; path=/';
                  document.cookie = '_gcl=; Max-Age=0; path=/';
                  document.cookie = '_gcl_au=; Max-Age=0; path=/';
                  
                  // Wywołaj zdarzenie gdy użytkownik odrzuci Google Ads
                  window.dispatchEvent(new CustomEvent('klaro:consent', {
                    detail: {
                      services: { 'google-ads': false }
                    }
                  }))
                }
              })
            }

            return services
          })()
        })
      }
    })
  }
}
</script>

<style scoped>
#klaro {
  z-index: 9999;
}
</style> 